/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  type AdjustProformaInput,
  type BulkConvertProformasInput,
  type ConvertProformaInput,
  type GenerateProformaCycleInput,
  type ProformaStatus,
} from '@therapy/shared-validators';

import {
  type Proforma,
  type ProformaConvertResult,
  type ProformaCycleResult,
  proformasApi,
} from '../api/proformas.js';

const KEY = ['proformas'] as const;

function useInvalidate(): () => void {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: KEY });
  };
}

export function useMyProformas(): UseQueryResult<readonly Proforma[]> {
  return useQuery({ queryKey: [...KEY, 'mine'], queryFn: () => proformasApi.mine() });
}

export function useProformas(status?: ProformaStatus): UseQueryResult<readonly Proforma[]> {
  return useQuery({
    queryKey: [...KEY, 'all', status ?? 'any'],
    queryFn: () => proformasApi.list(status),
  });
}

export function useGenerateProformaCycle(): UseMutationResult<
  ProformaCycleResult,
  Error,
  GenerateProformaCycleInput
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: GenerateProformaCycleInput) => proformasApi.generate(input),
    onSuccess: invalidate,
  });
}

export function useAdjustProforma(): UseMutationResult<
  Proforma,
  Error,
  { id: string; input: AdjustProformaInput }
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { id: string; input: AdjustProformaInput }) =>
      proformasApi.adjust(v.id, v.input),
    onSuccess: invalidate,
  });
}

export function useSendProforma(): UseMutationResult<Proforma, Error, string> {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (id: string) => proformasApi.send(id), onSuccess: invalidate });
}

export function useApproveProforma(): UseMutationResult<Proforma, Error, string> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => proformasApi.approve(id),
    onSuccess: invalidate,
  });
}

export function useDeclineProforma(): UseMutationResult<
  Proforma,
  Error,
  { id: string; reason: string }
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { id: string; reason: string }) => proformasApi.decline(v.id, v.reason),
    onSuccess: invalidate,
  });
}

export function useConvertProforma(): UseMutationResult<
  Proforma,
  Error,
  { id: string; input: ConvertProformaInput }
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { id: string; input: ConvertProformaInput }) =>
      proformasApi.convert(v.id, v.input),
    onSuccess: invalidate,
  });
}

export function useBulkConvertProformas(): UseMutationResult<
  ProformaConvertResult,
  Error,
  BulkConvertProformasInput
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: BulkConvertProformasInput) => proformasApi.convertBulk(input),
    onSuccess: invalidate,
  });
}
